import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  TextField,
  Stack,
  useMediaQuery,
  Grid
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatDate';
import api from '../../services/api';
import ServerDataTable from '../ServerDataTable/ServerDataTable';
import JsonDataDisplay from '../common/JsonDataDisplay';
import StatusBadge from '../StatusBadge';

export default function SyncedTrackingTab() {
  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // State for search and pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    synced_at_start: '',
    synced_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    synced_at_start: '',
    synced_at_end: ''
  });

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters
    const filtersFromURL = {
      synced_at_start: params.get('synced_at_start') || '',
      synced_at_end: params.get('synced_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Synchronize filterForm with appliedFilters when filter dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams();

    // Add filter parameters to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());
    params.set('search', currentSearchTerm || '');
    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
      setPageIndex(0); // Reset to first pageIndex when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch tracking records with updated pagination
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['trackingRecords', pageIndex, pageSize, debouncedSearchTerm, appliedFilters],
    queryFn: async () => {
      const params = {
        offset: pageIndex * pageSize,
        limit: pageSize,
        search: debouncedSearchTerm || undefined,
        startDate: appliedFilters.synced_at_start || undefined,
        endDate: appliedFilters.synced_at_end || undefined
      };

      const response = await api.get('/tracking-sync/tracking-records', {
        params
      });

      // If we're not on the first pageIndex and the total results would make current pageIndex empty
      const totalPages = Math.ceil(
        response.data.data.pagination?.total / pageSize
      );
      if (pageIndex > 0 && pageIndex >= totalPages) {
        setPageIndex(0);
        // Re-fetch with new pageIndex
        return refetch();
      }

      return {
        records: response.data.data.items || [],
        pagination: response.data.data.pagination || {
          total: 0,
          limit: pageSize,
          offset: 0,
          hasMore: false
        }
      };
    }
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, newSearchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  const handleOpenViewDialog = (data) => {
    setViewData(data);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Clear filters and close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      synced_at_start: '',
      synced_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: true,
      align: 'center'
    },
    {
      field: 'order_number',
      headerName: 'Order Number',
      width: 150,
      sortable: true
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => (
        <Typography variant='body2' fontFamily='monospace'>
          {value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'tracking_number',
      headerName: 'Tracking Number',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => (
        <Typography variant='body2' fontFamily='monospace'>
          {value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'tracking_provider',
      headerName: 'Provider',
      width: 120,
      sortable: true,
      renderCell: ({ value }) => (
        <StatusBadge customize={true} customLabel={value} customColor='info' />
      )
    },
    {
      field: 'created_at',
      headerName: 'Synced At',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => (
        <Typography variant='body2'>
          {formatDate(value)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton
            size='small'
            onClick={() => handleOpenViewDialog(row)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color='error' align='center'>
            Error loading tracking records: {error.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const records = data?.records || [];

  return (
    <Box>
      <ServerDataTable
        data={records || []}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={data?.pagination?.total || 0}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isFetching}
        isError={error}
        error={error}
        noDataMessage='No tracking records found'
        totalPages={Math.ceil(data?.pagination?.total / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by Order Number, SKU, or Tracking Number'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some((value) => value !== '') && (
                <Chip
                  size='small'
                  label={isFetching ? '' : Object.values(appliedFilters).filter((value) => value !== '').length}
                  color='secondary'
                  sx={{ ml: 1 }}
                  disabled={isFetching}
                />
              )}
            </Button>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isFetching}>
              Refresh
            </Button>
          </Box>
        }
      />

      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6' component='div'>Tracking Record Details</Typography>
            <Box>
              <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
                {isDialogMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton onClick={handleCloseViewDialog}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Order Number
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.order_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div' sx={{ wordBreak: 'break-all !important' }}>
                  Item Code
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant='body1' gutterBottom sx={{ mr: 1 }} color='text.secondary' component='div'>
                    {viewData.item || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  UOM
                </Typography>
                <Box component='div' sx={{ mt: 1 }}>
                  <StatusBadge customize={true} customLabel={viewData.uom} customColor='info' />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  SKU
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.sku || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Tracking Number
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.tracking_number || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Tracking Provider
                </Typography>
                <Box component='div' sx={{ mt: 1 }}>
                  <StatusBadge customize={true} customLabel={viewData.tracking_provider} customColor='info' />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Synced At
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData} title='Product Data' />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Tracking Records
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography variant='subtitle2'>Synced At Range</Typography>
            <TextField
              name='synced_at_start'
              label='From Date'
              type='date'
              value={filterForm.synced_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name='synced_at_end'
              label='To Date'
              type='date'
              value={filterForm.synced_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
